import { friendlyApiErrorMessage } from 'src/app/services/api-error-notifier.service';
import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Optional, Output, ViewChild } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { ApiService } from 'src/app/services/api.service';
import { FilterService } from 'src/app/services/filter.service';
import { PageDataService } from 'src/app/services/page-data.service';
import { NewFilterService } from 'src/app/services/new-filter.service';

@Component({
  selector: 'app-dynamic-chart-widget',
  templateUrl: './dynamic-chart-widget.component.html',
  styleUrls: ['./dynamic-chart-widget.component.scss'],
  standalone: false
})
export class DynamicChartWidgetComponent implements OnInit, OnDestroy {
  static key = 100828;

  @Input('item') item: any;
  @Input('pageKey') pageKey: any;
  @Output('onSizeChange') onSizeChange = new EventEmitter<any>();
  @ViewChild('chartDiv', { static: true }) chartDiv!: ElementRef;

  config: any;
  data: any[] = [];
  heading: any;
  isDataLoaded = false;
  reqSubscription: Subscription[] = [];
  error_name: any;

  constructor(
    private filterService: FilterService,
    private httpClient: HttpClient,
    private apiService: ApiService,
    @Optional() private pageDataService: PageDataService,
    private newFilterService: NewFilterService,
  ) {}

  ngOnInit(): void {
    const observer = new ResizeObserver((e: any) => {
      this.onSizeChange.emit(e[0].contentRect.height);
    });
    observer.observe(this.chartDiv.nativeElement);
    this.loadChartConfig();

    const cfg = this.item?.config;
    const dataMode = cfg?.['data_mode'] || 'widget'; // 'page' | 'widget'

    if (dataMode === 'widget') {
      // Widget mode: component fetches its own data independently
      this.reqSubscription.push(
        this.filterService.filterQuery.subscribe(() => {
          this.loadApiData();
        })
      );
    } else if (dataMode === 'page' && this.pageDataService) {
      // Page mode: data comes from centralized PageDataService
      this.reqSubscription.push(
        this.pageDataService.loading$.subscribe(loading => {
          if (loading) this.isDataLoaded = false;
        })
      );

      const listOfApi = cfg?.['list_of_api'];
      const apiKey = typeof listOfApi === 'object' ? listOfApi?.api_key : listOfApi;
      const stream$ = apiKey
        ? this.pageDataService.getDataStream(String(apiKey))
        : this.pageDataService.getCommonDataStream();

      // The stream replays its current value on subscribe; an empty replay is just the
      // placeholder before the first fetch, so skip it. Every later emission (including
      // empty results and failed calls) must reach the chart so it never shows stale data.
      let isReplay = true;
      this.reqSubscription.push(
        stream$.subscribe(res => {
          const replay = isReplay;
          isReplay = false;
          if (replay && !(res && res.length > 0)) return;
          this.isDataLoaded = true;
          this.data = res || [];
          this.error_name = this.pageDataService.getFetchError(apiKey);
        })
      );
    }

    // Show By toggle: reload with the selected report type.
    // Page mode re-fetches centrally in PageDataService and arrives via the data stream.
    if (dataMode === 'widget') {
      this.reqSubscription.push(
        this.newFilterService.showBy.subscribe((value: any) => {
          this.isDataLoaded = false;
          this.loadApiData(value);
        })
      );
    }
  }

  loadChartConfig() {
    this.heading = this.item.config['report_heading'];
    this.config = {
      xAxisLabel: this.item.config['x_axis']?.['api_resp_column_display_nm'] || '',
      yAxisLabel: this.item.config['y_axis']?.['api_resp_column_display_nm'] || '',
      xAxisVar: this.item.config['x_axis']?.['api_resp_column_nm'] || '',
      yAxisVar: this.item.config['y_axis']?.['api_resp_column_nm'] || '',
      chartType: this.item.config['chart_type']?.value || '',
    };
  }

  loadApiData(reportType?: string) {
    let api = this.item.config['list_of_api'] || {};

    if (api && (this.filterService.isEmptyObject(this.filterService.baseQuery) == false)) {
      this.isDataLoaded = false;
      let obj: any = { "api_key": api?.api_key ?? api };

      let query = Object.assign({}, this.filterService.baseQuery, obj);
      query["report_typ"] = reportType ? reportType : this.filterService.report_type;
      query['module_key'] = this.apiService.getModuleKey();
      let endPoint = this.item.config?.["widget_endpoint"] || '';

      this.reqSubscription.push(
        this.httpClient.post(`${this.apiService.apiBaseUrl}/${endPoint || this.apiService.executionUrl}`, query).subscribe((res: any) => {
          this.isDataLoaded = true;
          this.data = res == null ? [] : res;
          this.error_name = '';
        }, (err: any) => {
          this.isDataLoaded = true;
          this.data = [];
          this.error_name = friendlyApiErrorMessage(err);
          console.log(err);
        })
      );
    }
  }

  ngOnDestroy(): void {
    this.reqSubscription.forEach(res => res.unsubscribe());
  }
}
